import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface FacilitySummary {
  id: string;
  name: string;
  facilityType: string;
  status: string;
  suburb: string | null;
  state: string;
  timezone?: string;
  regionId?: string | null;
}

export interface ServiceAreaSummary {
  id: string;
  name: string;
  status: string;
  facilityId: string | null;
  facilityName?: string | null;
  suburb?: string | null;
  state?: string | null;
}

interface FacilityState {
  /** Currently selected facility (null = all facilities / tenant-wide view) */
  currentFacility: FacilitySummary | null;
  /** Currently selected home-care/SAH service area (null = all service areas / not branch-scoped) */
  currentServiceArea: ServiceAreaSummary | null;
  /** All facilities available to the user */
  facilities: FacilitySummary[];
  /** All home-care/SAH service areas available to the user */
  serviceAreas: ServiceAreaSummary[];
  /** Whether the facility list has been loaded */
  isLoaded: boolean;
  /** IDs of facilities the current user is assigned to (empty for admins who see all) */
  assignedFacilityIds: string[];
  /** IDs of service areas the current user is assigned to (empty for admins who see all) */
  assignedServiceAreaIds: string[];
  /** Whether assignedFacilityIds has been loaded from the server */
  facilityAccessLoaded: boolean;

  setFacilities: (facilities: FacilitySummary[]) => void;
  setServiceAreas: (serviceAreas: ServiceAreaSummary[]) => void;
  selectFacility: (facility: FacilitySummary | null) => void;
  selectServiceArea: (serviceArea: ServiceAreaSummary) => void;
  /** Clear every persisted location dimension before an auth/tenant transition. */
  clearScope: () => void;
  getCurrentFacilityId: () => string | null;
  getCurrentServiceAreaId: () => string | null;
  setAssignedFacilityIds: (ids: string[]) => void;
  setAssignedServiceAreaIds: (ids: string[]) => void;
  /** Check whether the user has access to a specific facility */
  isUserAssigned: (facilityId: string) => boolean;
  /** Check whether the user has access to a specific service area */
  isUserAssignedToServiceArea: (serviceAreaId: string) => boolean;
}

export const useFacilityStore = create<FacilityState>()(
  persist(
    (set, get) => ({
      currentFacility: null,
      currentServiceArea: null,
      facilities: [],
      serviceAreas: [],
      isLoaded: false,
      assignedFacilityIds: [],
      assignedServiceAreaIds: [],
      facilityAccessLoaded: false,

      setFacilities: (facilities) => {
        const current = get().currentFacility;
        // If current selection is no longer valid, reset
        const stillValid = current ? facilities.some((f) => f.id === current.id) : true;
        set({
          facilities,
          isLoaded: true,
          currentFacility: stillValid ? current : null,
        });
      },

      setServiceAreas: (serviceAreas) => {
        const current = get().currentServiceArea;
        const stillValid = current ? serviceAreas.some((area) => area.id === current.id) : true;
        set({
          serviceAreas,
          currentServiceArea: stillValid ? current : null,
        });
      },

      selectFacility: (facility) =>
        set(
          facility
            ? { currentFacility: facility, currentServiceArea: null }
            : { currentFacility: null, currentServiceArea: null },
        ),

      selectServiceArea: (serviceArea) =>
        set({ currentFacility: null, currentServiceArea: serviceArea }),

      clearScope: () =>
        set({
          currentFacility: null,
          currentServiceArea: null,
          facilities: [],
          serviceAreas: [],
          isLoaded: false,
          assignedFacilityIds: [],
          assignedServiceAreaIds: [],
          facilityAccessLoaded: false,
        }),

      getCurrentFacilityId: () => get().currentFacility?.id ?? null,

      getCurrentServiceAreaId: () => get().currentServiceArea?.id ?? null,

      setAssignedFacilityIds: (ids) =>
        set({ assignedFacilityIds: ids, facilityAccessLoaded: true }),

      setAssignedServiceAreaIds: (ids) => set({ assignedServiceAreaIds: ids }),

      isUserAssigned: (facilityId) => {
        const { assignedFacilityIds, facilityAccessLoaded } = get();
        // Before load completes, deny access to prevent flash of unauthorized content
        if (!facilityAccessLoaded) return false;
        // After load: empty array means admin (no restrictions)
        if (assignedFacilityIds.length === 0) return true;
        return assignedFacilityIds.includes(facilityId);
      },

      isUserAssignedToServiceArea: (serviceAreaId) => {
        const { assignedServiceAreaIds, facilityAccessLoaded } = get();
        if (!facilityAccessLoaded) return false;
        if (assignedServiceAreaIds.length === 0) return true;
        return assignedServiceAreaIds.includes(serviceAreaId);
      },
    }),
    {
      name: 'statura-facility',
      partialize: (state) => ({
        currentFacility: state.currentFacility,
        currentServiceArea: state.currentServiceArea,
      }),
    },
  ),
);
